import { useState, useEffect } from 'react';
import { cleanFilename } from '../utils/fileUtils';
import { Document } from '../types/contractor.types';
import dataProvider from '../../services/api';

interface UseDocumentsProps {
  userId: number | null;
  handleErrorWithLogout: (error: any) => void;
  showSuccessMessage: (message: string) => void;
}

export const useDocuments = ({ userId, handleErrorWithLogout }: UseDocumentsProps) => {

  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(null);

  const fetchUserDocuments = async (userId: number) => {
    try {
      const { data } = await dataProvider.getUserDocuments({ userId });
      const cleanedDocuments = data.map((doc: any) => ({
        ...doc,
        filename: cleanFilename(doc.filename),
      }));
      setDocuments(cleanedDocuments);
      if (cleanedDocuments.length > 0) {
        setSelectedDocumentId(cleanedDocuments[0].documentId);
      }
    } catch (error: any) {
      handleErrorWithLogout(error);
      setDocuments([]);
    }
  };

  const handleVerifyUserChange = async (userId: number) => {
    try {
      await dataProvider.setContractorVerified({ userId, verified: true });
      fetchUserDocuments(userId);
    } catch (error: any) {
      handleErrorWithLogout(error);
    }
  };

  const handleDocumentStatusChange = async (
    userId: number,
    documentId: number,
    status: number,
    notes: string = 'approve'
  ) => {
    try {
      await dataProvider.setDocumentStatus({
        userId,
        documentId,
        status,
        notes,
      });
      fetchUserDocuments(userId);
    } catch (error: any) {
      handleErrorWithLogout(error);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchUserDocuments(userId);
    } else {
      setDocuments([]);
      setSelectedDocumentId(null);
    }
  }, [userId]);

  const selectedDocument = documents.find(doc => doc.documentId === selectedDocumentId) || null;

  return {
    documents,
    selectedDocumentId,
    selectedDocument,
    setSelectedDocumentId,
    handleDocumentStatusChange,
    handleVerifyUserChange,
    refetchDocuments: userId ? () => fetchUserDocuments(userId) : () => {},
  };
};